import type { ErrorCopy } from '@shared/errors/catalog';

/**
 * The `error_code` values this surface meets.
 *
 * It is the one page in the console that talks to **every** service, so the map
 * is keyed on the suffix rather than on one namespace: nine services raise the
 * same shapes with nine prefixes, and writing `AUTH_FORBIDDEN`,
 * `CONTENT_FORBIDDEN`, `ASSETS_FORBIDDEN` … nine times over would be one
 * sentence duplicated nine ways and nine chances to let them drift.
 *
 * **Never inline a backend code in JSX.** The code is a log key; what reaches
 * the operator is the sentence, and the `X-Request-ID` is what they quote.
 */

const BY_SUFFIX: Readonly<Record<string, ErrorCopy>> = {
  /**
   * The sharpest one on this page. `/settings` is `admin`-only while most of the
   * console admits an `operator`, so this is the code an operator meets on
   * arrival — and the copy names the role rather than the route, because the
   * route is not the thing that is wrong.
   */
  FORBIDDEN: {
    message: 'تهيئة التشغيل من صلاحيّة المدير وحده، ولا يحمله حسابك.',
    tone: 'warning',
  },
  NOT_FOUND: {
    message: 'لم نعثر على هذا المفتاح. قد تكون الخدمة أُصدرت من جديد بمفاتيح مختلفة — أعد تحميل الصفحة.',
    tone: 'warning',
  },
  /**
   * The service validates the value before the write and refuses what it cannot
   * parse, rather than accepting it and failing at its next boot. That refusal
   * is the whole reason the console does not validate values itself: a second
   * validator that disagreed with the service's would be worse than none.
   */
  CONFIG_VALUE_INVALID: {
    message: 'الخدمة لا تقبل هذه القيمة. راجع شكلها المذكور في الوصف ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  BAD_INPUT: {
    message: 'تعذّر إتمام الكتابة بهذه القيمة. راجعها ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  CONFLICT: {
    message: 'تغيّر المفتاح بينما كانت الصفحة مفتوحة. أعد التحميل ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  RATE_LIMITED: {
    message: 'كتابات كثيرة في وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  UPSTREAM_FAILED: {
    message: 'الخدمة غير متاحة الآن. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  UPSTREAM_TIMEOUT: {
    message: 'تأخّرت الخدمة عن الردّ. أعد تحميل الصفحة للاطّلاع على النتيجة.',
    tone: 'neutral',
  },
};

/**
 * The nine namespaces, expanded from the suffix table.
 *
 * The expansion happens once, at module load, so the resolver stays the shared
 * one — a lookup by exact `error_code` — and no call site has to know that this
 * page's codes are assembled rather than written out.
 */
const NAMESPACES = [
  'AUTH',
  'CONTENT',
  'ASSETS',
  'SEARCH',
  'ENGAGEMENT',
  'ANALYTICS',
  'NOTIFICATION',
  'FINANCE',
  'SUPPORT',
] as const;

export const errors: Readonly<Record<string, ErrorCopy>> = Object.fromEntries(
  NAMESPACES.flatMap((namespace) =>
    Object.entries(BY_SUFFIX).map(([suffix, entry]) => [`${namespace}_${suffix}`, entry]),
  ),
);
